/**
 * Session state for the SPA.
 *
 * The client-side permission checks here decide what to RENDER. They are not security — every
 * endpoint enforces the same permission server-side, and a user who edits their session in devtools
 * gets a 403, not data. Hiding a nav item the user cannot use is a courtesy, not a control.
 */
import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from './api';

export type Me = {
  username: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  locationScope: string[];
  mustChangePassword: boolean;
};

type AuthValue = {
  user: Me | null;
  loading: boolean;
  can: (...anyOf: string[]) => boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const r = await api.get<{ user: Me }>('/api/v1/auth/me');
        return r.user;
      } catch (e) {
        // 401 is the normal "not logged in" answer, not a failure worth retrying.
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const user = data ?? null;

  const can = useCallback(
    (...anyOf: string[]) => !!user && anyOf.some((p) => user.permissions.includes(p)),
    [user],
  );

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  const logout = useCallback(async () => {
    // Revoke server-side first, but do not let a network failure strand the user in a session
    // they have asked to leave — the local state is cleared either way.
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Already logged out, or offline. Clearing locally is still the right thing to do.
    }

    // ORDER MATTERS, and this is the whole bug.
    //
    // `queryClient.clear()` DETACHES the mounted `me` observer. Anything written to the cache
    // afterwards is never seen by the component, so the provider kept reporting the old user and
    // the shell sat on "Loading…" — the sign-out button appeared to do nothing. Verified: after
    // clear() the cache held `null` while the UI still rendered the signed-in state.
    //
    // So: write the logged-out state FIRST, while the observer is still attached and will
    // re-render, then drop the other cached queries WITHOUT touching `me`.
    qc.setQueryData(['me'], null);

    // Everything except the session — otherwise the next user to sign in on this machine briefly
    // sees the previous user's data from cache.
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }, [qc]);

  return (
    <Ctx.Provider value={{ user, loading: isLoading, can, refresh, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
